import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { VideoIcon, MenuIcon, CloseIcon } from "./icons/NavbarIcons";
import { TopBar } from "./navbar/TopBar";
import { DesktopMenu } from "./navbar/DesktopMenu";
import { MobileMenu } from "./navbar/MobileMenu";
import { CONTACT_INFO } from "./navbar/constants";
import { scrollToSection } from "../utils/scroll";

function Navbar() {
  const location = useLocation();
  const isServicesPage = location.pathname.endsWith("/services");
  const isHomePage = location.pathname === "/";
  const isCareersPage = location.pathname.endsWith("/careers");

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const previousScrollPosition = useRef(0);
  const scrollFrame = useRef<number | null>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  // Close menu when modal triggers a close-navbar event
  useEffect(() => {
    const handleCloseNavbar = () => {
      setIsMenuOpen(false);
      setIsDropdownOpen(false);
    };
    window.addEventListener("close-navbar", handleCloseNavbar);
    return () => {
      window.removeEventListener("close-navbar", handleCloseNavbar);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollFrame.current !== null) return;

      scrollFrame.current = window.requestAnimationFrame(() => {
        const currentScrollPos = window.scrollY;
        const isScrollingDown = currentScrollPos > previousScrollPosition.current;

        setScrolled(currentScrollPos > 50);
        setVisible(!(isScrollingDown && currentScrollPos > 100));
        previousScrollPosition.current = currentScrollPos;
        scrollFrame.current = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollFrame.current !== null) {
        window.cancelAnimationFrame(scrollFrame.current);
      }
    };
  }, []);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const syncHeaderHeight = () => {
      document.documentElement.style.setProperty(
        "--site-header-height",
        `${header.offsetHeight}px`,
      );
    };

    syncHeaderHeight();

    const observer = new ResizeObserver(syncHeaderHeight);
    observer.observe(header);
    window.addEventListener("resize", syncHeaderHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncHeaderHeight);
    };
  }, []);

  const navbarClasses =
    isMenuOpen
      ? "bg-white border-b border-gray-200"
      : isHomePage
      ? "bg-white/70 backdrop-blur-md border-b border-gray-200"
      : "bg-white/95 backdrop-blur-lg border-b border-gray-200 shadow-sm";

  const iconColor =
    isServicesPage || isCareersPage ? "text-gray-900" : "text-blue-400";

  const handleNavigation = (sectionId: string) => {
    scrollToSection(sectionId);
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 w-full z-[100] ${navbarClasses} ${
          scrolled ? "shadow-md" : ""
        } transition-transform duration-300 ${
          visible ? "transform-none" : "-translate-y-full"
        }`}
      >
        {/* Desktop Top Info Bar */}
        <TopBar iconColor={iconColor} />

        {/* Main Header Row */}
        <div className="flex items-center justify-between py-3.5 md:py-2 lg:py-2.5 xl:py-3 2xl:py-5 px-3 sm:px-4 lg:px-6 xl:px-12 2xl:px-32">
          {/* Mobile Zoom button (Left-aligned) */}
          <div className="flex md:hidden w-1/4 justify-start">
            <a
              href={CONTACT_INFO.zoom}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-1 bg-blue-600 text-white px-2.5 py-1.5 rounded-full text-xs font-semibold shadow-sm hover:bg-blue-700 transition-colors"
              title="Join Zoom Meeting"
            >
              <VideoIcon className="w-3.5 h-3.5" />
              <span>Zoom</span>
            </a>
          </div>

          {/* Logo (Centered on mobile, Left-aligned on desktop) */}
          <div className="flex w-1/2 justify-center md:w-auto md:justify-start">
            <Link to="/">
              <div className="text-2xl font-bold">
                <img
                  src="/assets/home/adhd-logo-168.webp"
                  srcSet="/assets/home/adhd-logo-168.webp 168w, /assets/home/adhd-logo-240.webp 240w"
                  sizes="(min-width: 1536px) 144px, (min-width: 1280px) 112px, (min-width: 1024px) 96px, (min-width: 768px) 80px, 96px"
                  width={168}
                  height={60}
                  fetchPriority="high"
                  decoding="async"
                  className="w-24 h-auto md:w-20 lg:w-24 xl:w-28 2xl:w-36"
                  alt="Logo"
                />
              </div>
            </Link>
          </div>

          {/* Mobile Hamburger menu (Right-aligned) */}
          <div className="flex md:hidden w-1/4 justify-end">
            <button
              onClick={toggleMenu}
              className="text-gray-700 hover:text-blue-500 focus:outline-none"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <CloseIcon className="w-6 h-6" />
              ) : (
                <MenuIcon className="w-6 h-6" />
              )}
            </button>
          </div>

          {/* Desktop Navigation Menu */}
          <DesktopMenu
            isDropdownOpen={isDropdownOpen}
            toggleDropdown={toggleDropdown}
            dropdownRef={dropdownRef}
            handleNavigation={handleNavigation}
          />
        </div>

        {/* Mobile Menu Dropdown drawer */}
        <MobileMenu
          isMenuOpen={isMenuOpen}
          isDropdownOpen={isDropdownOpen}
          toggleDropdown={toggleDropdown}
          handleNavigation={handleNavigation}
          setIsMenuOpen={setIsMenuOpen}
        />
      </header>
    </>
  );
}

export default Navbar;
